import { ReactElement, useMemo } from "react";
import {
	FormItem,
	FormattedText,
	Select,
	SelectOption,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { SharePointAuthenticationType } from "client/Api/SharePointSettings/sharepointSettings";
import { IValueInputAttachable } from "client/Forms/attachable";

export type SharePointAuthenticationTypeFormItemProps = {
	attachable: IValueInputAttachable<SharePointAuthenticationType | null>;
	availableAuthenticationTypes: readonly SharePointAuthenticationType[];
};

export const SharePointAuthenticationTypeFormItem = (props: SharePointAuthenticationTypeFormItemProps): ReactElement | null => {
	const {
		attachable,
		availableAuthenticationTypes
	} = props;

	const authenticationOptions = useMemo(
		(): readonly SelectOption<SharePointAuthenticationType>[] => {
			return availableAuthenticationTypes.map(
				(x): SelectOption<SharePointAuthenticationType> => ({
					value: x,
					text: localize(authenticationTypeToLocalizationKeyMap[x])
				})
			);
		},
		[availableAuthenticationTypes]
	);

	// If there is exactly 1 authentication type available preselect it and don't bother the user with a dropdown. Unless it's ADFS,
	// in which case we want it to be visible, but disabled, with the ADFS hint.
	const isAuthenticationTypeDisabled: boolean = authenticationOptions.length === 1
		&& authenticationOptions[0].value === SharePointAuthenticationType.activeDirectoryFederationServices;

	const isAuthenticationTypeVisible: boolean = authenticationOptions.length > 1
		|| isAuthenticationTypeDisabled;

	if (!isAuthenticationTypeVisible) {
		return null;
	}

	return (
		<FormItem label={localize("_AuthenticationType")} isRequired>
			<Select
				{...attachable.attachToSelect()}
				options={authenticationOptions}
				isDisabled={isAuthenticationTypeDisabled}
				menuTitle={localize("_AuthenticationType")}
			/>

			<ValidationError {...attachable.attachToValidationError()} />

			{attachable.value === SharePointAuthenticationType.activeDirectoryFederationServices && (
				<p>
					<FormattedText
						text={localize("_SharePointADSFAuthenticationMessage")}
						thingsToReplace={[
							{
								what: "{ProductName}",
								filler: userConfiguration().branding
									.productName
							}
						]}
					/>
				</p>
			)}
		</FormItem>
	);
};
SharePointAuthenticationTypeFormItem.displayName = "SharePointAuthenticationTypeFormItem";

const authenticationTypeToLocalizationKeyMap: { [key in SharePointAuthenticationType]: LocalizationKey; } = {
	[SharePointAuthenticationType.online]: "_SharePointOnline",
	[SharePointAuthenticationType.activeDirectoryFederationServices]: "_ActiveDirectoryFederationServices",
	[SharePointAuthenticationType.oAuth2]: "_OAuth2"
};
